import React, { useState, useEffect } from "react";
import Button from "../Buttons";
import { CONSTANTS } from "../../constants/Constants";
import LoadSpinner from "../LoadingSpinner";

interface OTPScreenProps {
  length: number;
  otp_error?: string;
  on_change: (otp: string) => void;
  on_click: (e: any) => void;
  isLoading: boolean;
  resendOTP: (e: React.FormEvent) => void;
}

const OTPScreen: React.FC<OTPScreenProps> = ({
  length,
  on_change,
  on_click,
  otp_error,
  isLoading,
  resendOTP,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [timer, setTimer] = useState<number>(60);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(true);

  // Handle Timer Countdown
  useEffect(() => {
    if (isTimerRunning && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
  
      return () => clearInterval(countdown);
    } else {
      setIsTimerRunning(false);
    }
  }, [isTimerRunning, timer]);
  

  const startTimer = () => {
    setTimer(60); // Reset the timer
    setIsTimerRunning(true);
  };

  const handleResendOTP = (e: React.FormEvent) => {
    resendOTP(e);
    startTimer();
  };

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    on_change(newOtp.join(""));

    // Automatically focus the next box
    if (value && index < length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData("text").slice(0, length);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = Array(length)
      .fill("")
      .map((_, i) => pastedData[i] || "");
    setOtp(newOtp);
    on_change(newOtp.join(""));
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  return (
    <div className="mt-10 h-[calc(100vh-200px)] flex items-center justify-center">
      <div className="h-72 w-[500px] flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="flex flex-col items-center">
          <h2 className="text-lg font-medium mb-2">
            {CONSTANTS.ENTER_OTP_TXT}
          </h2>
          <p className="text-gray-600 mb-4 text-sm">{CONSTANTS.OTP_SENT_TXT}</p>
          <div className="flex space-x-2">
            {otp.map((_, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                maxLength={1}
                value={otp[index]}
                onChange={(e) => handleChange(e.target.value, index)}
                onPaste={handlePaste}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="w-12 h-12 border-2 border-gray-300 rounded-lg text-center text-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            ))}
          </div>
          <div className="mt-4 w-full relative">
            <Button
              is_disable={isLoading}
              btn_label={CONSTANTS.VERIFY_TXT}
              on_click_handler={on_click}
            />
            {isLoading && <LoadSpinner classes={"absolute top-0 left-40"} />}
          </div>
          <small className="text-error">{otp_error}</small>
          <p className="mt-2 text-gray-500 text-sm">
            {CONSTANTS.DIDNT_RCVD_CODE}
            {isTimerRunning ? (
              <span className="text-gray-400 ml-2">
                ({timer}s)
              </span>
            ) : (
              <span
                className="text-primary cursor-pointer"
                onClick={handleResendOTP}
              >
                {" "}
                {CONSTANTS.RESEND_OTP}
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPScreen;
